<template>
    <v-card>
        <v-card-title>
            {{$t('event_home.features')}}
            <v-spacer></v-spacer>
            <v-btn v-if="isEditOrderMode" color="red" dark class="ml-3" @click="cancelEditOrder">
                {{$t('Cancel')}}
                <v-icon right>mdi-close-circle-outline</v-icon>
            </v-btn>
            <v-btn v-if="isEditOrderMode" color="green" dark class="ml-3" @click="saveEditOrder">
                {{$t('Save')}}
                <v-icon right>mdi-check</v-icon>
            </v-btn>
            <v-btn v-if="!isEditOrderMode" color="primary" @click="initEditOrder">
                {{$t('Reorder')}}
                <v-icon right>mdi-swap-vertical</v-icon>
            </v-btn>
            <div class="button-spacer"></div>
            <v-btn  v-if="!isEditOrderMode" color="primary" @click="initAddFeature">
                {{$t('add')}}
                <v-icon right>mdi-plus</v-icon>
            </v-btn>
        </v-card-title>
        <v-data-table :headers="headers"
                      :items="tableHomeFeatures"
                      :loading="isHomeFeaturePending"
                      @pagination="currentPagination"
                      disable-sort>
            <template v-slot:item.scroller="{value}">
                <div v-show="isEditOrderMode" class="handle"><v-btn icon ><v-icon>drag_handle{{value}}</v-icon></v-btn></div>
                <div v-show="!isEditOrderMode"></div>
            </template>
            <template v-slot:item.createdAt="{value}">
                {{DateTime.fromISO(value).toFormat('yyyy-MM-dd HH:mm')}}
            </template>
            <template v-slot:item.updatedAt="{value}">
                {{DateTime.fromISO(value).toFormat('yyyy-MM-dd HH:mm')}}
            </template>
            <template v-slot:item.icon="{value}">
                <v-img :src="value.src" max-width="5rem" max-height="5rem"></v-img>
            </template>
            <template v-slot:item.feature="{value}">
                {{value ? value.name : ''}}
            </template>
            <template v-slot:item.actions="{item}">
                <v-btn color="primary" class="ma-2"
                       @click="initEditFeature(item)">
                    {{$t('edit')}}
                    <v-icon right>mdi-pencil</v-icon>
                </v-btn>
                <v-btn color="red" dark class="ma-2"
                       @click="deleteFeature(item)">
                    {{$t('delete')}}
                    <v-icon right>mdi-delete</v-icon>
                </v-btn>
            </template>
        </v-data-table>

        <v-dialog v-model="editFeatureDialogShown" max-width="600px" persistent="">
            <v-card>
                <v-card-title>
                    {{$t('home_feature.title')}}
                </v-card-title>
                <v-card-text>
                    <v-container>
                        <v-form v-model="isEditFeatureFormValid">

                            <v-row>
                                <v-col>
                                    <v-text-field outlined
                                                  :label="$t('home_feature.order')"
                                                  :placeholder="$t('home_feature.order')"
                                                   :rules="[featureOrderRule]"
                                                  v-model="editingFeature.order"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col>
                                    <v-text-field outlined
                                                  :label="$t('home_feature.name')"
                                                  :placeholder="$t('home_feature.name')"    
                                                  v-model="editingFeature.name"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col>
                                    <v-select outlined
                                              clearable
                                              :label="$t('home_feature.feature')"
                                              :placeholder="$t('home_feature.feature')"
                                              :items="featureOptionsFiltered"
                                              @change="onChangeFeatureOptions"
                                              v-model="editingFeature.featureId"></v-select>
                                </v-col>
                            </v-row>
                            <v-row align="center">
                                <v-col>
                                    <v-file-input accept="image/*"
                                                :label="$t('home_feature.icon') + ' ' + $t('home_feature.recommendedIconSize')"
                                                :disabled="isDisabled"
                                                :clearable="!isDisabled"
                                                v-model="editingFeature.icon">
                                        <template v-slot:selection="{file}">
                                            <v-img :src="getFileUrl(file)"
                                                max-width="250px"
                                                max-height="250px"></v-img>
                                        </template>
                                    </v-file-input>
                                </v-col>
                                <v-col align="center">
                                    <v-radio-group v-model="useDefaultIcon" :mandatory="true" @change="onChangeRadioSelection">
                                        <v-radio :label="$t('home_feature.default_icon')" value="default"></v-radio>
                                        <v-radio :label="$t('home_feature.personal_icon')" value="personal"></v-radio>
                                    </v-radio-group>
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-container>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="secondary" @click="confirmCancelItemDialogShown = true">{{$t('cancel')}}</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="saveFeature"
                           :disabled="!isEditFeatureFormValid">{{$t('save')}}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <PopupAlert
            v-model="confirmCancelItemDialogShown"
            title="Warning"
            message="Do you wish to cancel your amendments?"
            :onYes="cancelEditFeatureItem"
            :onNo="function() {confirmCancelItemDialogShown=false}"
            ></PopupAlert>
        <v-overlay :z-index="999" v-model="overlay">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
    </v-card>
</template>
<style scoped>
    .button-spacer {
        width:10px;
    }
</style>
<script>
    import {computed, ref, onMounted,watch} from "@vue/composition-api";
    import {useFind} from "feathers-vuex";
    import {DateTime} from "luxon";
    import * as lodash from "lodash"
    import Sortable from "sortablejs";
    import PopupAlert from "../../components/PopupAlert";

    export default {
        name: 'EventHomeFeatureList',
        props: {
            homeId: String,
        },
        components:{PopupAlert},
        setup(props, {root}) {
            const {HomeFeature, Feature, EventFeature} = root.$FeathersVuex.api;
            const eventId = computed(() => root.$route.params.eventId);

            // Features
            const homeFeatureParams = computed(() => {
                return {
                    query: {
                        fHomeId: props.homeId,
                        $sort: {order: 1},
                    }
                }
            });
            const homeFeatureQueryWhen = computed(() => {
                return !!props.homeId;
            });
            const {items: homeFeatures, isPending: isHomeFeaturePending} = useFind({
                model: HomeFeature,
                params: homeFeatureParams,
                queryWhen: homeFeatureQueryWhen,
            });
            // Data Table
            const headers = ref([
                {text: '', value:'scroller',width:80},
                {text: root.$t('home_feature.order'), value: 'order'},
                {text: root.$t('home_feature.icon'), value: 'icon'},
                {text: root.$t('home_feature.name'), value: 'name'},
                {text: root.$t('home_feature.feature'), value: 'feature'},
                {text: '', value: 'actions'},
            ]);
            
            watch(isHomeFeaturePending,(newval,oldval)=>{
                if(!newval && oldval){
                    editOrderHomeFeatures.value = homeFeatures.value
                }
            })
            // Add / Edit Feature
            const editFeatureDialogShown = ref(false);
            const confirmCancelItemDialogShown = ref(false);
            const isEditFeatureFormValid = ref(false);
            const editingFeature = ref(new HomeFeature({fHomeId: props.homeId}));
            const isNewFeature = ref(false);
            const useDefaultIcon = ref("default");
            const tempPersonalIcon = ref(null);
            const isDisabled = ref(false);

            const currentStart = ref(0);

            const currentPagination = function(e){
                currentStart.value = e.pageStart
            }
            const initAddFeature = function () {
                useDefaultIcon.value = "default"
                isDisabled.value = true
                isNewFeature.value = true;
                tempPersonalIcon.value = null // reset temp personal icon

                editingFeature.value = new HomeFeature({fHomeId: props.homeId});
                editFeatureDialogShown.value = true;
                editingFeature.value.order = homeFeatures.value.length + 1
                editingFeature.value.isDefaultIcon = true;
            };
            const initEditFeature = function (feature) {
                editingFeature.value = lodash.cloneDeep(feature);
                editFeatureDialogShown.value = true;
                // Check if the editing feature is currently using default icon or not
                const tempfeature = getFeatureOptionById(editingFeature.value.featureId)
                if(tempfeature.img.src === editingFeature.value.icon.src){
                    useDefaultIcon.value = "default"
                    isDisabled.value = true
                    editingFeature.value.isDefaultIcon = true
                }else{
                    useDefaultIcon.value = "personal"
                    editingFeature.value.isDefaultIcon = false
                    isDisabled.value = false
                }
                isNewFeature.value = false
            };
            const deleteFeature = function (feature) {
                if (confirm(root.$t('home_feature.confirm_delete'))) {
                    feature.remove().then(() => {
                    }, root.$handleFeatherError.bind(root));
                }
            };

            // EventFeature
            const eventFeatureParams = computed(() => {
                return {
                    query: {
                        eventId: eventId.value,
                    }
                }
            });
            const {items: eventFeatures, isPending: isEventFeaturePending} = useFind({
                model: EventFeature,
                params: eventFeatureParams,
            });
            const existingEventFeatureIds = computed(() => eventFeatures.value.map(ef => ef.featureId));

            const featureParams = computed(() => {
                return {
                    query: {
                        id: {
                            $in: existingEventFeatureIds.value,
                        }
                    }
                }
            });
            const {items: features, isPending: isFeaturePending} = useFind({model: Feature, params: featureParams});
            const featureOptions = computed(() => {
                return features.value.map(f => {
                    return {text: f.name, value: f.id}
                });
            });

            const featureOptionsFiltered = computed(()=>{
                let listFiltered = []
                let listContained = []
                if(homeFeatures.value && homeFeatures.value.length>0){
                    listContained = homeFeatures.value.map(f =>{
                        if(f.feature==null){
                            return null
                        }
                        return f.feature.name
                    });
                }
                for(let i=0;i<features.value.length;i++){
                    if(isNewFeature.value){
                        if(listContained.includes(features.value[i].name)){
                            continue
                        }else{
                            listFiltered.push({text:features.value[i].name,value:features.value[i].id,img: features.value[i].default_icon})
                        }
                    }else{
                        if(listContained.includes(features.value[i].name) && editingFeature.value.feature!=null && features.value[i].name !== editingFeature.value.feature.name){
                            continue 
                        }else{
                            listFiltered.push({text:features.value[i].name,value:features.value[i].id, img: features.value[i].default_icon})
                        }
                    }
                }
                return listFiltered
            })

            const getFeatureOptionById = function(id){
                for(let i=0;i<featureOptionsFiltered.value.length;i++){
                    if(featureOptionsFiltered.value[i].value===id){
                        return featureOptionsFiltered.value[i]
                    }
                }
                return null
            }
            const saveFeature = async function () {
                try {
                    if (editingFeature.value.icon instanceof File) {
                        editingFeature.value.icon = {src: await root.$uploadFile(editingFeature.value.icon)};
                    }
                    if (!editingFeature.value.icon) {
                        editingFeature.value.icon = null;
                    }
                    editingFeature.value.save().then(() => {
                        HomeFeature.get(editingFeature.value.id);
                        editFeatureDialogShown.value = false;
                        root.$store.dispatch('showSnackBar', root.$t('home_feature.save_successful'));
                    }, root.$handleFeatherError.bind(root));
                    tempPersonalIcon.value = null
                } catch (e) {
                    root.$handleFeatherError(e);
                }
            };

            const cancelEditFeatureItem = function(){
                editFeatureDialogShown.value = false;
                confirmCancelItemDialogShown.value = false;
                isNewFeature.value = false;
                tempPersonalIcon.value = null
            }

            const featureOrderRule = function(value){
                const onlyNumberRule =  /^[0-9]+$/;
                return onlyNumberRule.test(value) || 'Invalid Number';
            }

            const featureOrderRule2 = function(value){
                let length = homeFeatures.value.length;

                if(isNewFeature.value){
                    return ((value==length+1) && value>=0) || 'Number out of range';
                }else{
                    return (value>=0 && (value<=length)) || 'Number out of range';
                }
            }
            const onChangeFeatureOptions = function(value){
                // if(useDefaultIcon.value==="personal")return
                editingFeature.value.icon = getFeatureOptionById(value).img
                useDefaultIcon.value = "default";
                editingFeature.value.isDefaultIcon = true
            }
            const onChangeRadioSelection = function(value){
                if(value==="default"){
                    isDisabled.value = true
                    useDefaultIcon.value = "default";
                    tempPersonalIcon.value = editingFeature.value.icon
                    editingFeature.value.icon = getFeatureOptionById(editingFeature.value.featureId).img
                    editingFeature.value.isDefaultIcon = true
                }else{
                    isDisabled.value = false
                    useDefaultIcon.value = "personal";
                    editingFeature.value.isDefaultIcon = false
                    if(tempPersonalIcon.value!=null){
                        editingFeature.value.icon = tempPersonalIcon.value
                    }else{
                        editingFeature.value.icon = null
                    }
                }
            }

            const editOrderHomeFeatures = ref([]);
            const isEditOrderMode = ref(false);
            const didOrderChange = ref(false);
            const overlay = ref(false);


            const initEditOrder = function(){
                didOrderChange.value = false
                isEditOrderMode.value = true
                editOrderHomeFeatures.value = lodash.cloneDeep(homeFeatures.value)
            }

            const cancelEditOrder = function(){
                isEditOrderMode.value = false;
            }

            const saveEditOrder = async function(){
                if(!didOrderChange.value){
                    isEditOrderMode.value = false
                    return;
                }

                overlay.value = true
                for(let i=0;i<editOrderHomeFeatures.value.length;i++){
                    await editOrderHomeFeatures.value[i].save().then(()=>{
                        HomeFeature.get(editOrderHomeFeatures.value[i].id)
                    })
                }
                isEditOrderMode.value = false
                overlay.value = false;
            }
            onMounted(()=>{
                let table = document.querySelector('.v-data-table tbody');
                Sortable.create(table,
                    {
                        handle:".handle", // Look for class with name "handle"
                        onEnd({newIndex,oldIndex}){
                            newIndex +=  currentStart.value;
                            oldIndex +=  currentStart.value;
                            const rowSelected = editOrderHomeFeatures.value.splice(oldIndex,1)[0];
                            editOrderHomeFeatures.value.splice(newIndex,0,rowSelected);

                            didOrderChange.value = true
                            for(let i=0;i<editOrderHomeFeatures.value.length;i++){
                                editOrderHomeFeatures.value[i].order = i+1
                            }
                        }
                    }
                )
            })

            const tableHomeFeatures = computed(()=>{
                return isEditOrderMode.value?editOrderHomeFeatures.value:homeFeatures.value;
            })
            return {
                homeFeatures,
                tableHomeFeatures,
                isHomeFeaturePending,

                headers,

                editFeatureDialogShown,
                confirmCancelItemDialogShown,
                initAddFeature,
                initEditFeature,
                deleteFeature,
                isEditFeatureFormValid,
                editingFeature,
                featureOptions,
                onChangeFeatureOptions,
                saveFeature,
                cancelEditFeatureItem,

                featureOrderRule,
                featureOrderRule2,

                featureOptionsFiltered,
                isNewFeature,
                DateTime,
                getFileUrl: root.$getFileURL,

                useDefaultIcon,
                onChangeRadioSelection,
                isDisabled,

                currentPagination,

                initEditOrder,
                saveEditOrder,
                cancelEditOrder,
                isEditOrderMode,
                overlay
            }
        }
    }
</script>
